import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AlertTriangle, Plus, Pencil, Trash2, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { EVIDENCE_STATUSES, EVIDENCE_STATUS_COLORS, type EvidenceStatus } from "../config/wiseboardConfig";

interface EvidenceRow {
  id: string;
  case_id: string;
  hypothesis_id: string;
  claim: string;
  status: string;
  source: string | null;
  date_sourced: string | null;
  confidence: number | null;
  caveat: string | null;
  hypothesis_link: string | null;
  pass_number: number;
  updated_by_project: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface EvidenceMatrixTrackerProps {
  caseId: string;
  currentProject?: string;
  readOnly?: boolean;
  refreshKey?: number;
}

const EMPTY_FORM = {
  hypothesis_id: "",
  claim: "",
  status: "NOT_FOUND" as string,
  source: "",
  date_sourced: "",
  confidence: 50,
  caveat: "",
  hypothesis_link: "",
  pass_number: 1,
};

export default function EvidenceMatrixTracker({ caseId, currentProject, readOnly = false, refreshKey }: EvidenceMatrixTrackerProps) {
  const [rows, setRows] = useState<EvidenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [filterPass, setFilterPass] = useState<string>("ALL");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("wiseboard_evidence_matrix")
      .select("*")
      .eq("case_id", caseId)
      .order("hypothesis_id", { ascending: true })
      .order("pass_number", { ascending: true });
    if (data) setRows(data as EvidenceRow[]);
    setLoading(false);
  }, [caseId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Continuity warnings
  const pass1Ids = new Set(rows.filter(r => r.pass_number === 1).map(r => r.hypothesis_id));
  const pass2Ids = new Set(rows.filter(r => r.pass_number === 2).map(r => r.hypothesis_id));
  const hasPass2 = pass2Ids.size > 0;
  const missingInPass2 = hasPass2 ? [...pass1Ids].filter(id => !pass2Ids.has(id)) : [];
  const newInPass2 = hasPass2 ? [...pass2Ids].filter(id => !pass1Ids.has(id)) : [];

  // Filtering
  const filtered = rows.filter(r => {
    if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
    if (filterPass !== "ALL" && r.pass_number !== Number(filterPass)) return false;
    return true;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, pass_number: currentProject === "P2_PASS2" ? 2 : 1, });
    setDialogOpen(true);
  };

  const openEdit = (row: EvidenceRow) => {
    setEditingId(row.id);
    setForm({
      hypothesis_id: row.hypothesis_id,
      claim: row.claim,
      status: row.status,
      source: row.source || "",
      date_sourced: row.date_sourced ? row.date_sourced.split("T")[0] : "",
      confidence: row.confidence ?? 50,
      caveat: row.caveat || "",
      hypothesis_link: row.hypothesis_link || "",
      pass_number: row.pass_number,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Please sign in"); return; }

    const payload = {
      case_id: caseId,
      hypothesis_id: form.hypothesis_id.trim(),
      claim: form.claim.trim(),
      status: form.status,
      source: form.source || null,
      date_sourced: form.date_sourced || null,
      confidence: form.confidence,
      caveat: form.caveat || null,
      hypothesis_link: form.hypothesis_link || null,
      pass_number: form.pass_number,
      updated_by_project: currentProject || null,
      user_id: user.id,
    };

    if (!payload.hypothesis_id || !payload.claim) {
      toast.error("Hypothesis ID and Claim are required");
      return;
    }

    let error;
    if (editingId) {
      const { error: e } = await supabase.from("wiseboard_evidence_matrix").update(payload).eq("id", editingId);
      error = e;
    } else {
      const { error: e } = await supabase.from("wiseboard_evidence_matrix").insert(payload);
      error = e;
    }

    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? "Evidence updated" : "Evidence added");
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("wiseboard_evidence_matrix").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Evidence removed");
    load();
  };

  const statusBadge = (status: string) => {
    const colors = EVIDENCE_STATUS_COLORS[status as EvidenceStatus] || "bg-muted text-muted-foreground";
    return <Badge className={`text-[10px] font-mono ${colors}`}>{status.replace(/_/g, " ")}</Badge>;
  };

  // Stats
  const totalClaims = rows.length;
  const found = rows.filter(r => r.status === "DATA_FOUND").length;
  const notFound = rows.filter(r => r.status === "NOT_FOUND").length;
  const stale = rows.filter(r => r.status === "POTENTIALLY_STALE").length;
  const assumptions = rows.filter(r => r.status === "ASSUMPTION").length;

  if (loading) return <div className="text-sm text-muted-foreground p-4">Loading evidence matrix...</div>;

  return (
    <Card>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
            Evidence Matrix
            <Badge variant="outline" className="text-[10px]">{totalClaims} claims</Badge>
          </CardTitle>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Stats Bar */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge className="bg-green-100 text-green-800">Found: {found}</Badge>
            <Badge className="bg-red-100 text-red-800">Not Found: {notFound}</Badge>
            <Badge className="bg-yellow-100 text-yellow-800">Stale: {stale}</Badge>
            <Badge className="bg-gray-100 text-gray-800">Assumptions: {assumptions}</Badge>
          </div>

          {/* Continuity Warnings */}
          {hasPass2 && missingInPass2.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-yellow-800 dark:text-yellow-300">Continuity Warning:</span>{" "}
                <span className="text-yellow-700 dark:text-yellow-400">
                  {missingInPass2.length} hypothesis ID(s) from Pass 1 not updated in Pass 2:{" "}
                  {missingInPass2.join(", ")}
                </span>
              </div>
            </div>
          )}
          {hasPass2 && newInPass2.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-950 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-blue-800 dark:text-blue-300">New in Pass 2:</span>{" "}
                <span className="text-blue-700 dark:text-blue-400">
                  {newInPass2.length} new hypothesis ID(s) added in Pass 2: {newInPass2.join(", ")}
                </span>
              </div>
            </div>
          )}

          {/* Filters + Add */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-7 text-xs w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  {EVIDENCE_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Select value={filterPass} onValueChange={setFilterPass}>
              <SelectTrigger className="h-7 text-xs w-[120px]">
                <SelectValue placeholder="Pass" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Passes</SelectItem>
                <SelectItem value="1">Pass 1</SelectItem>
                <SelectItem value="2">Pass 2</SelectItem>
              </SelectContent>
            </Select>
            {!readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={openCreate}>
                <Plus className="h-3 w-3 mr-1" /> Add Evidence
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2 font-medium">ID</th>
                  <th className="text-left p-2 font-medium">Claim</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Source</th>
                  <th className="text-center p-2 font-medium">Conf.</th>
                  <th className="text-center p-2 font-medium">Pass</th>
                  <th className="text-left p-2 font-medium">Updated by</th>
                  {!readOnly && <th className="p-2 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={readOnly ? 7 : 8} className="p-4 text-center text-muted-foreground">No evidence items yet</td></tr>
                )}
                {filtered.map(row => (
                  <tr key={row.id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-2 font-mono font-semibold whitespace-nowrap">{row.hypothesis_id}</td>
                    <td className="p-2 max-w-[200px]">
                      <div className="truncate">{row.claim}</div>
                      {row.caveat && <div className="text-[10px] text-muted-foreground italic mt-0.5">⚠ {row.caveat}</div>}
                    </td>
                    <td className="p-2">{statusBadge(row.status)}</td>
                    <td className="p-2 max-w-[140px] truncate text-muted-foreground">{row.source || "—"}</td>
                    <td className="p-2 text-center">
                      {row.confidence != null ? (
                        <span className={`font-semibold ${row.confidence >= 70 ? "text-green-600" : row.confidence >= 40 ? "text-yellow-600" : "text-red-600"}`}>
                          {row.confidence}%
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-2 text-center">
                      <Badge variant="outline" className="text-[10px]">P{row.pass_number}</Badge>
                    </td>
                    <td className="p-2 text-muted-foreground">{row.updated_by_project || "—"}</td>
                    {!readOnly && (
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(row)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(row.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Create/Edit Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Evidence" : "Add Evidence"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Hypothesis ID *</Label>
                    <Input className="h-8 text-sm" placeholder="H1, H2.1…" value={form.hypothesis_id} onChange={e => setForm(f => ({ ...f, hypothesis_id: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Pass</Label>
                    <Select value={String(form.pass_number)} onValueChange={v => setForm(f => ({ ...f, pass_number: Number(v) }))}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Pass 1</SelectItem>
                        <SelectItem value="2">Pass 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Claim *</Label>
                  <Textarea className="text-sm" rows={2} placeholder="The claim or hypothesis statement…" value={form.claim} onChange={e => setForm(f => ({ ...f, claim: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EVIDENCE_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date Sourced</Label>
                    <Input type="date" className="h-8 text-sm" value={form.date_sourced} onChange={e => setForm(f => ({ ...f, date_sourced: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Source</Label>
                  <Input className="h-8 text-sm" placeholder="URL, report name, etc." value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Confidence: {form.confidence}%</Label>
                  <Slider min={0} max={100} step={5} value={[form.confidence]} onValueChange={([v]) => setForm(f => ({ ...f, confidence: v }))} />
                </div>
                <div>
                  <Label className="text-xs">Caveat</Label>
                  <Input className="h-8 text-sm" placeholder="Any caveats or limitations…" value={form.caveat} onChange={e => setForm(f => ({ ...f, caveat: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Hypothesis Link</Label>
                  <Input className="h-8 text-sm" placeholder="Link to related hypothesis…" value={form.hypothesis_link} onChange={e => setForm(f => ({ ...f, hypothesis_link: e.target.value }))} />
                </div>
                <Button className="w-full" onClick={handleSave}>{editingId ? "Update" : "Add"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      )}
    </Card>
  );
}
